import { useMutation, useQuery } from '@tanstack/vue-query';
import { type MaybeRefOrGetter, toRef } from 'vue';
import { phoneService } from '@/api/phone.service';
import { formatDistanceToNowStrict } from 'date-fns';
import type { SendBookingLinkParamsDto } from '@carvia/ros-client-types';
import type { MutationDefaultsContext } from './query-client';

export const useRecentPhoneNumbers = (enabled: MaybeRefOrGetter<boolean> = true) => {
  return useQuery({
    queryKey: ['recent-phone-numbers'],
    enabled: toRef(enabled),
    queryFn: async () => {
      const recentCalls = await phoneService.getRecentCalls();
      // Dedupe by number while keeping most recent occurrence
      const latestByNumber = new Map<string, { number: string; date: Date }>();
      for (const call of recentCalls ?? []) {
        const number = call.number.toString();
        const date = call.endedAt ? new Date(call.endedAt) : new Date(call.startedAt);
        const prev = latestByNumber.get(number);
        if (!prev || (date && new Date(prev.date).getTime() < date.getTime())) {
          latestByNumber.set(number, { number, date });
        }
      }
      return Array.from(latestByNumber.values())
        .sort((a, b) => b.date.getTime() - a.date.getTime())
        .map(({ number, date }) => ({
          value: number,
          label: `${number} (${formatDistanceToNowStrict(date, { addSuffix: true })})`,
        }));
    },
  });
};

export const useDialCall = () => {
  return useMutation({
    mutationKey: ['dial-call'],
    mutationFn: (phoneNumber: string) => phoneService.dial(phoneNumber),
    onMutate: () => ({ showSuccessToast: false }),
  });
};

export const useSendBookingLink = () => {
  return useMutation<
    { status: 'OK' },
    unknown,
    { phoneNumber: string; params: SendBookingLinkParamsDto },
    MutationDefaultsContext
  >({
    mutationKey: ['send-booking-link'],
    mutationFn: (payload) => phoneService.sendBookingLink(payload),
    onMutate: () => ({ successMessage: 'Sent booking link' }),
  });
};
